/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { getFormValues, isValid } from 'redux-form';
import _ from 'lodash';

import styles from './styles.module.scss';
import UsageInfo from './UsageInfo';
import WorklistHeader from './WorklistHeader';
import DataLoader from '../../../components/DataLoader';
import Divider from '../../../components/Divider';
import ActionButton from '../../../components/ActionButton';
import ActionButtons from '../../../components/ActionButtons';
import Modal from '../../../components/Modal';
import Table from '../../../components/Table';
import TrackListHeader from '../../../components/TrackListHeader';
import TrackListRow from '../../../components/TrackListRow';
import ProductionForm from '../../../forms/ProductionForm';
import UsageForm from '../../../forms/UsageForm';
import actions from '../../../redux/actions';
import { getIsPlaying } from '../../../redux/reducers/playerQueue';
import { getTrackSearchParams } from '../../../redux/reducers/trackSearch';
import { getSessionUsageId } from '../../../redux/reducers/usages';
import {
  getWorklist,
  worklistHasPendingChanges,
  getWorklistItems,
  getWorklistItemsToCheckout,
  getIsFetchingWorklistItems,
} from '../../../redux/reducers/worklists';
import protonesUtils from '../../../utils/protones';

import type {
  Fetchable,
  SimplifiedTrack,
  SimplifiedMedia,
  MergedLicenseComponent,
  Worklist,
  WorklistItem,
  ReduxState,
  Translate,
  Usage,
  SaveUsageParams,
  UpdateUsageParams,
  UpdateWorklistParams,
  UpdateWorklistItemParams,
  SaveProductionParams,
  SearchParams,
  TrackSearchParams,
} from '../../../types';
import type { ContextRouter } from 'react-router-dom';

const mapStateToProps = (state: ReduxState, ownProps) => {
  const worklistId = parseInt(protonesUtils.fromRoutes.worklistId(ownProps.location.pathname), 10);
  return {
    headerFormValues: getFormValues('worklistHeader')(state),
    headerFormValid: isValid('worklistHeader')(state),
    usageFormValues: getFormValues('worklistUsage')(state),
    usageFormValid: isValid('worklistUsage')(state),
    currentTrackId: state.playerQueue.currentTrackId,
    isPlaying: getIsPlaying(state),
    worklist: getWorklist(state),
    worklistId,
    items: getWorklistItems(state),
    itemsToCheckout: getWorklistItemsToCheckout(state).filter(
      item => item.worklistId === worklistId
    ),
    isFetchingWorklistItems: getIsFetchingWorklistItems(state),
    sessionUsageId: getSessionUsageId(state).data || undefined,
    hasPendingChanges: worklistHasPendingChanges(state),
    trackSearchParams: getTrackSearchParams(state),
  };
};

type Props = ContextRouter & {
  headerFormValues: Object,
  headerFormValid: boolean,
  usageFormValues: Object,
  usageFormValid: boolean,
  currentTrackId: number,
  isPlaying: boolean,
  hasPendingChanges: boolean,
  worklist: Fetchable<Worklist>,
  worklistId: number,
  items: Fetchable<WorklistItem[]>,
  itemsToCheckout: WorklistItem[],
  sessionUsageId?: number,
  playTracks: typeof actions.playTracks,
  downloadTracks: typeof actions.downloadTracks,
  askQuotation: typeof actions.askQuotation,
  createCartItemsFromWorklistItems: typeof actions.createCartItemsFromWorklistItems,
  deleteWorklistItems: typeof actions.deleteWorklistItems,
  fetchWorklist: typeof actions.fetchWorklist,
  fetchWorklistItems: typeof actions.fetchWorklistItems,
  isFetchingWorklistItems: boolean,
  fetchUsageList: typeof actions.fetchUsageList,
  performTrackSearch: typeof actions.performTrackSearch,
  trackSearchParams: TrackSearchParams,
  displayMessage: typeof actions.displayMessage,
  deleteWorklist: typeof actions.deleteWorklist,
  updateWl1: typeof actions.updateWl1,
  updateWl2: typeof actions.updateWl2,
  saveUsage: typeof actions.saveUsage,
  deleteUsage: typeof actions.deleteUsage,
  saveWorklist: typeof actions.saveWorklist,
  saveWorklistItems: typeof actions.saveWorklistItems,
  saveProduction: typeof actions.saveProduction,
  t: Translate,
};

type State = {
  selectedComponentsByItem: { [itemId: string]: string[] },
  isSavePresetModalVisible: boolean,
  isProductionModalVisible: boolean,
  usagePresetInitialValues: Usage | null,
  expandedItems: {
    [itemId: number]: boolean,
  },
};

const getDefaultSelectedComponentsByItem = (items: WorklistItem[] | any) => {
  const selectedComponentsByItem = {};
  if (!_.isArray(items)) {
    return selectedComponentsByItem;
  }
  items.forEach(item => {
    const hasComponents = item.mergedComponents != null && item.mergedComponents.length > 0;
    if (!hasComponents) {
      return;
    }
    const hasUnpricedComponents = (item.mergedComponents || []).some(
      c => !protonesUtils.getCanBuyLicenseComponent(c)
    );
    const pricedComponents = (item.mergedComponents || []).filter(c =>
      protonesUtils.getCanBuyLicenseComponent(c)
    );
    const hasPricedComponents = pricedComponents.length > 0;
    if (hasUnpricedComponents && hasPricedComponents) {
      selectedComponentsByItem[item.id] = pricedComponents.map(c => c.id);
    }
  });
  return selectedComponentsByItem;
};

class WorklistShow extends React.Component<Props, State> {
  state = {
    selectedComponentsByItem: getDefaultSelectedComponentsByItem(this.props.items.data),
    isSavePresetModalVisible: false,
    isProductionModalVisible: false,
    usagePresetInitialValues: null,
    expandedItems: {},
    sorting: undefined,
  };

  get expandedItemIds(): number[] {
    return _.map(_.keys(_.pickBy(this.state.expandedItems, v => v === true)), v => Number(v));
  }

  itemsSentToCart: WorklistItem[] = [];

  componentDidMount() {
    const { displayMessage, itemsToCheckout, worklistId, t } = this.props;
    if (itemsToCheckout.length > 0) {
      displayMessage(t('ITEMS_TO_CHECKOUT_NEED_USAGE'), false, true);
    }
    if (worklistId) {
      this._fetchWorklistItems(worklistId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const worklistId = _.get(this.props, 'worklist.data.id');
    const worklistItems = _.get(this.props, 'items.data', []);
    const { itemsToCheckout } = this.props;
    const itemsToCheckoutIds = itemsToCheckout.map(item => item.id);
    const itemsSentToCartIds = this.itemsSentToCart.map(item => item.id);
    const worklistItemsToBuy = worklistItems.filter(
      item =>
        protonesUtils.getCanBuyWorklistItem(item) &&
        itemsToCheckoutIds.includes(item.id) &&
        !itemsSentToCartIds.includes(item.id)
    );
    if (worklistItemsToBuy.length > 0) {
      this._createCartItemsFromWorklistItems(worklistItemsToBuy).then(() =>
        protonesUtils.goTo(this.props, protonesUtils.routes.cart.items.find())
      );
    }
    if (
      worklistId &&
      (!_.isEqual(worklistId, _.get(prevProps, 'worklist.data.id')) ||
        this.props.hasPendingChanges !== prevProps.hasPendingChanges)
    ) {
      this._fetchWorklistItems(worklistId);
    }
    if (!_.isEqual(this.props.items.data, prevProps.items.data)) {
      this.setState({
        selectedComponentsByItem: getDefaultSelectedComponentsByItem(this.props.items.data),
      });
    }
  }

  _toggleExpandSelectedItems = () => {
    const { selectedComponentsByItem } = this.state;
    const items = this.props.items.data || [];
    const { expandedItems } = this.state;
    const selectedItems = items.filter(item => selectedComponentsByItem.hasOwnProperty(item.id));
    const selectedItemIds = selectedItems.map(i => i.id);
    const isAllExpanded = selectedItemIds.every(id => this.expandedItemIds.includes(id));
    for (const itemId of selectedItemIds) {
      expandedItems[itemId] = !isAllExpanded;
    }
    this.setState({ expandedItems });
    this.forceUpdate();
  };

  async _fetchWorklistItems(id: number) {
    await this.props.fetchWorklistItems(id);
  }

  _toggleWorklistItem = (itemId: string) => {
    const { selectedComponentsByItem } = this.state;
    const { items } = this.props;
    const item = items.data.find(item => item.id === itemId);
    const itemComponentIds = ((item && item.mergedComponents) || []).map(component => component.id);
    const nextSelectedComponentsByItem = Object.assign({}, selectedComponentsByItem);
    if (selectedComponentsByItem.hasOwnProperty(itemId)) {
      delete nextSelectedComponentsByItem[itemId];
    } else {
      nextSelectedComponentsByItem[itemId] = itemComponentIds;
    }
    this.setState(state => ({ ...state, selectedComponentsByItem: nextSelectedComponentsByItem }));
  };

  _handleToggleComponent = (componentId: string, itemId: string) => {
    const { selectedComponentsByItem } = this.state;
    const itemComponentIds = selectedComponentsByItem[itemId] || [];
    const selectedComponentIds = itemComponentIds.includes(componentId)
      ? itemComponentIds.filter(id => id !== componentId)
      : [...itemComponentIds, componentId];
    const nextSelectedComponentsByItem = Object.assign({}, selectedComponentsByItem);
    if (selectedComponentIds.length > 0) {
      nextSelectedComponentsByItem[itemId] = selectedComponentIds;
    } else {
      delete nextSelectedComponentsByItem[itemId];
    }
    this.setState(state => ({ ...state, selectedComponentsByItem: nextSelectedComponentsByItem }));
  };

  _toggleSelectAllTracks = () => {
    const { selectedComponentsByItem } = this.state;
    const { items } = this.props;
    if (items.data) {
      const isSelectedAll =
        items.data.length !== 0 &&
        Object.keys(selectedComponentsByItem).length === items.data.length;
      if (isSelectedAll) {
        this.setState({ selectedComponentsByItem: {} });
      } else {
        const nextSelectedComponentsByItem = {};
        items.data.forEach(item => {
          nextSelectedComponentsByItem[item.id] = (item.mergedComponents || []).map(
            component => component.id
          );
        });
        this.setState({ selectedComponentsByItem: nextSelectedComponentsByItem });
      }
    }
  };

  _deleteWorklistItems = (items: WorklistItem[], worklist: Worklist) => {
    this.props.deleteWorklistItems(items, worklist, (deletedIds: number[]) => {
      this._fetchWorklistItems(worklist.id);
    });
  };

  _downloadTracks = (tracks: SimplifiedTrack[]) => {
    this.props.downloadTracks(tracks);
  };

  _playTracks = (tracks: SimplifiedTrack[]) => {
    this.props.playTracks(tracks);
  };

  _createCartItemsFromWorklistItems = async (items: WorklistItem[]) => {
    const { createCartItemsFromWorklistItems, displayMessage, t } = this.props;
    if (!items || items.length === 0) {
      return;
    }
    const itemsToBuy = items
      .filter(protonesUtils.getCanBuyWorklistItem)
      .map(protonesUtils.filterOutOptionalComponents);
    const itemsToQuote = items
      .filter(protonesUtils.getCanAskQuotationForWorklistItem)
      .map(protonesUtils.filterOutOptionalComponents);
    const canQuoteItems = itemsToQuote.length > 0;
    const itemsWithPurchasableComponents = itemsToBuy.map(item => ({
      ...item,
      mergedComponents: (item.mergedComponents || []).filter(
        protonesUtils.getCanBuyLicenseComponent
      ),
    }));
    this.itemsSentToCart = _.uniqBy(
      [...this.itemsSentToCart, ...itemsWithPurchasableComponents],
      'id'
    );
    await createCartItemsFromWorklistItems(itemsWithPurchasableComponents);
    await this._fetchWorklistItems(items[0].worklistId);
    canQuoteItems && displayMessage(t('PURCHASE_MISSING_COMPONENTS'), false, true);
  };

  _askQuotationForWorklistItems = (items: WorklistItem[]) => {
    const itemsWithQuotableComponents = items.map(item => ({
      ...item,
      mergedComponents: (item.mergedComponents || []).filter(
        protonesUtils.getCanAskQuotationForLicenseComponent
      ),
    }));
    this.props.askQuotation(itemsWithQuotableComponents);
    protonesUtils.goTo(this.props, protonesUtils.routes.ondemand.find());
  };

  _handleMediaClick = (media: SimplifiedMedia) => {
    protonesUtils.goTo(this.props, protonesUtils.routes.medias.get(media.id));
  };

  _handleEditWorklist = (id: number) => {
    this.props.fetchWorklist(id);
    protonesUtils.goTo(this.props, protonesUtils.routes.worklists.edit(id));
  };

  _handleDeleteWorklist = (id: number) => {
    //eslint-disable-next-line
    if (confirm(this.props.t('ARE_YOU_SURE'))) {
      this.props.deleteWorklist(id, () => {
        this.props.fetchWorklistList();
        protonesUtils.goTo(this.props, protonesUtils.routes.worklists.find());
      });
    }
  };

  _getWorklistUsageParamsFromObject = (obj): UpdateUsageParams => {
    return protonesUtils.getUsageUpdateParamsFromObject(obj);
  };

  _handleSaveWorklist = async (id: number) => {
    const { headerFormValid, headerFormValues, usageFormValid, usageFormValues } = this.props;
    if (headerFormValid || usageFormValid) {
      const updateParams: UpdateWorklistParams = {
        id,
        name: _.get(headerFormValues, 'name', ''),
        description: _.get(headerFormValues, 'description', ''),
        usage: this._getWorklistUsageParamsFromObject(usageFormValues),
      };
      await this.props.saveWorklist(updateParams);
      await this._fetchWorklistItems(id);
    }
  };

  _handleRefreshWorklist = () => {
    const worklistId = _.get(this.props.worklist, 'data.id');
    if (worklistId) {
      this.props.fetchWorklist(worklistId);
      this._fetchWorklistItems(worklistId);
    }
  };

  _handleSetWorklist1 = async (id: number) => {
    await this.props.updateWl1(id);
    await this.props.fetchWorklist(id);
  };

  _handleSetWorklist2 = async (id: number) => {
    await this.props.updateWl2(id);
    await this.props.fetchWorklist(id);
  };

  _handleDeleteUsage = async (id: number) => {
    await this.props.deleteUsage(id);
  };

  _openSavePresetModal = (usageParams: SaveUsageParams) => {
    this.setState({
      isSavePresetModalVisible: true,
      usagePresetInitialValues: usageParams,
    });
  };

  _dismissSavePresetModal = () => {
    this.setState({ isSavePresetModalVisible: false, usagePresetInitialValues: null });
  };

  _openProductionModal = () => {
    this.setState({ isProductionModalVisible: true });
  };

  _dismissProductionModal = () => {
    this.setState({ isProductionModalVisible: false });
  };

  _handleUsagePresetSave = async (saveUsageParams: SaveUsageParams) => {
    this._dismissSavePresetModal();
    await this.props.saveUsage(this._getWorklistUsageParamsFromObject(saveUsageParams));
    await this.props.fetchUsageList();
  };

  _handleProductionCreate = async (params: SaveProductionParams) => {
    this._dismissProductionModal();
    await this.props.saveProduction(params);
    await this._handleRefreshWorklist();
  };

  _handleSaveWorklistItemUsage = (item: WorklistItem, usage: SaveUsageParams) => {
    // TODO: Edit
    // console. log(usage);
    const params: UpdateWorklistItemParams = {
      id: _.get(item, 'id', null),
      worklistId: _.get(item, 'worklistId', null),
      trackId: _.get(item, 'track.id', null),
      areaId: _.get(usage, 'areaId', null),
      channelId: _.get(usage, 'channelId', null),
      exclusiveEndDate: _.get(usage, 'exclusiveEndDate', null),
      endDate: _.get(usage, 'endDate', null),
      startDate: _.get(usage, 'startDate', null),
      exclusive: _.get(usage, 'exclusive', false),
      exclusiveNotes: _.get(usage, 'exclusiveNotes', ''),
      broadcastingId: _.get(usage, 'broadcastingId', null),
      notes: _.get(usage, 'notes', ''),
      productionId: _.get(usage, 'productionId', null),
      platformId: _.get(usage, 'platformId', null),
      territoryId: _.get(usage, 'territoryId', null),
      usageTypeId: _.get(usage, 'usageTypeId', null),
      broadcastTypeId: _.get(usage, 'broadcastTypeId', null),
      episode: _.get(usage, 'episode', ''),
    };
    this.props.saveWorklistItems([params]);
  };

  _renderActionButtons = (items: WorklistItem[], worklist: Worklist) => {
    const { displayMessage, isFetchingWorklistItems, t } = this.props;
    const tracks = items.map(item => item.track);
    const isDownloadable = tracks.every(track => track.downloadable === true);
    const itemsToBuy = items
      .filter(protonesUtils.getCanBuyWorklistItem)
      .map(protonesUtils.filterOutOptionalComponents);
    const itemsToQuote = items
      .filter(protonesUtils.getCanAskQuotationForWorklistItem)
      .map(protonesUtils.filterOutOptionalComponents);
    const canBuyItems = itemsToBuy.length > 0 && itemsToBuy.every(item => item.buyable);
    const canQuoteItems = itemsToQuote.length > 0;

    return (
      <ActionButtons
        buy={{
          fetching: isFetchingWorklistItems,
          onClick: canBuyItems ? () => this._createCartItemsFromWorklistItems(itemsToBuy) : null,
          tooltip: t('BUY'),
          data: canBuyItems ? [] : undefined,
        }}
        cannotBuy={{ fetching: isFetchingWorklistItems, data: canBuyItems ? undefined : () => {} }}
        download={{
          fetchable: isDownloadable,
          fetching: isFetchingWorklistItems,
          data: () =>
            isDownloadable
              ? this._downloadTracks(tracks)
              : displayMessage(t('TRACK_NOT_DOWNLOADABLE'), false),
        }}
        ask={{
          fetching: isFetchingWorklistItems,
          data: canQuoteItems ? () => this._askQuotationForWorklistItems(itemsToQuote) : null,
        }}
        delete={{
          fetching: isFetchingWorklistItems,
          data: () => this._deleteWorklistItems(items, worklist),
        }}
      />
    );
  };

  _setSortAndSubmit = (field: TrackSortingField) => {
    let sorting = { ...this.state.sorting };
    if (sorting) {
      if (sorting.field === field) {
        if (sorting.order === 'asc') {
          sorting = { field, order: 'desc' };
        } else if (sorting.order === 'desc') {
          sorting = undefined;
        }
      } else {
        sorting = { field, order: 'asc' };
      }
    } else {
      sorting = { field, order: 'asc' };
    }
    this.setState({ sorting });
  };

  _renderWorklist = data => {
    const worklist = data;
    const { hasPendingChanges, items, isFetchingWorklistItems, t } = this.props;
    const { sorting } = this.state;
    let sortedItems = items && items.data && items.data.length > 0 ? [...items.data] : [];
    const {
      selectedComponentsByItem,
      isSavePresetModalVisible,
      isProductionModalVisible,
      usagePresetInitialValues,
    } = this.state;
    const isSelectedAll = items.data
      ? Object.keys(selectedComponentsByItem).length === items.data.length && items.data.length > 0
      : false;
    const selectedItems = (items.data || []).filter(item =>
      selectedComponentsByItem.hasOwnProperty(item.id)
    );
    const selectedTracks = selectedItems.map(item => item.track);
    const isSelectedAllExpanded = selectedItems.every(item =>
      this.expandedItemIds.includes(item.id)
    );

    if (sorting) {
      sortedItems = _.orderBy(
        sortedItems,
        item =>
          sorting.field === 'title'
            ? item.track.title
            : sorting.field === 'performers' &&
              item.track.performers &&
              item.track.performers.length > 0
            ? item.track.performers[0].fullName
            : sorting.field === 'authors' && item.track.authors && item.track.authors.length > 0
            ? item.track.authors[0].fullName
            : null,
        [sorting.order]
      );
    } else {
      sortedItems = _.orderBy(sortedItems, ['modifiedDate'], ['desc']);
    }
    const imgSrc = protonesUtils.getWorklistCoverUrl(this.props.worklist);
    return (
      <div className={styles.container}>
        <div className={styles.actionButtons}>
          <ActionButton iconName={'close'} onClick={this.props.history.goBack} />
        </div>
        <WorklistHeader
          imgSrc={imgSrc}
          {...worklist}
          onDelete={() => this._handleDeleteWorklist(worklist.id)}
          onSelectAsWorklist1={() =>
            worklist.isWorklist1 ? null : this._handleSetWorklist1(worklist.id)
          }
          onSelectAsWorklist2={() =>
            worklist.isWorklist2 ? null : this._handleSetWorklist2(worklist.id)
          }
          onSave={() => this._handleSaveWorklist(worklist.id)}
        />
        <Divider />
        <UsageInfo
          {...worklist.usage}
          isReadOnlyUsage={
            !(items.data && items.data.some(worklistItem => !!worklistItem.usage.updatable))
          }
          onSave={() => this._handleSaveWorklist(worklist.id)}
          onSaveAsPreset={this._openSavePresetModal}
          onDeletePreset={this._handleDeleteUsage}
          onCreateProduction={this._openProductionModal}
        />
        {!!isSavePresetModalVisible && (
          <Modal title={t('SAVE_AS_PRESET')} onDismiss={this._dismissSavePresetModal}>
            <UsageForm
              form={'usagePreset'}
              initialValues={usagePresetInitialValues}
              isLoading={false}
              isPreset={true}
              usageNameVisibile={true}
              actionButtonsVisible={true}
              save={this._handleUsagePresetSave}
              canLeaveDirtyForm={true}
            />
          </Modal>
        )}
        {!!isProductionModalVisible && (
          <Modal title={t('CREATE_NEW_PRODUCTION')} onDismiss={this._dismissProductionModal}>
            <ProductionForm
              isCreate={true}
              // title={''}
              // isLoading={false}
              save={this._handleProductionCreate}
            />
          </Modal>
        )}
        <Divider />
        <Table.Table>
          <thead>
            <TrackListHeader
              isSelectable={true}
              showCheckbox={true}
              sorting={this.state.sorting}
              isSelected={isSelectedAll}
              isPlayerVisible={selectedTracks.length > 0}
              onPlayClick={() => this._playTracks(selectedTracks)}
              onDetailsExpand={this._toggleExpandSelectedItems}
              isDetailsExpanded={isSelectedAllExpanded}
              showActionButtons={Object.keys(selectedComponentsByItem).length > 0}
              onCheckboxClick={this._toggleSelectAllTracks}
              isLoading={isFetchingWorklistItems || hasPendingChanges}
              actionButtons={this._renderActionButtons(selectedItems, worklist)}
              hasUsage={true}
              onHeaderClick={this._setSortAndSubmit}
            />
          </thead>
          <tbody>
            {items.data == null ? <DataLoader loading /> : this._renderItems(sortedItems, worklist)}
          </tbody>
        </Table.Table>
      </div>
    );
  };

  _handleAskQuotationForComponents = (
    item: WorklistItem,
    mergedComponents: MergedLicenseComponent[]
  ) => {
    this._askQuotationForWorklistItems([item]);
  };

  _handleBuyComponents = async (item: WorklistItem, mergedComponents: MergedLicenseComponent[]) => {
    await this._createCartItemsFromWorklistItems([{ ...item, mergedComponents }]);
  };

  _handleTrackSearch = (params: SearchParams) => {
    const { performTrackSearch, trackSearchParams } = this.props;
    performTrackSearch(_.merge({}, trackSearchParams, params));
    protonesUtils.goTo(this.props, protonesUtils.routes.tracks.find());
  };

  _renderItems(itemsData: WorklistItem[], worklist: Worklist) {
    const { isPlaying, currentTrackId, isFetchingWorklistItems, t } = this.props;
    if (itemsData.length === 0) {
      return <div className={styles.centeredContent}>{t('EMPTY_WORKLIST')}</div>;
    } else {
      const { selectedComponentsByItem } = this.state;
      return itemsData.map(item => {
        const selectedComponentIds = selectedComponentsByItem[item.id] || [];
        const components = item.mergedComponents || [];
        const hasAllComponentsSelected =
          components.length > 0 && components.every(c => selectedComponentIds.includes(c.id));
        const hasSomeComponentsSelected =
          !hasAllComponentsSelected && components.some(c => selectedComponentIds.includes(c.id));
        const isLoading = isFetchingWorklistItems;
        const isExpanded = !!this.state.expandedItems[item.id];
        return (
          <TrackListRow
            key={item.id}
            track={{
              ...item.track,
              price: item.price,
            }}
            medias={item.track.medias}
            isWorklistItem={true}
            usage={item.usage}
            isReadOnlyUsage={!item.usage.updatable}
            components={components}
            onAskQuotationForComponents={this._handleAskQuotationForComponents.bind(this, item)}
            onBuyComponents={this._handleBuyComponents.bind(this, item)}
            onCreateProduction={this._openProductionModal}
            onSaveUsage={(usage: SaveUsageParams) => this._handleSaveWorklistItemUsage(item, usage)}
            isSelectable={true}
            isSelected={hasAllComponentsSelected}
            isSelectedIndeterminate={hasSomeComponentsSelected}
            selectedComponents={selectedComponentIds}
            onComponentCheckboxClick={componentId =>
              this._handleToggleComponent(componentId, item.id)
            }
            onComponentHeaderCheckboxClick={() => this._toggleWorklistItem(item.id)}
            isDetailsExpanded={isExpanded}
            isCurrentTrack={currentTrackId === item.track.id}
            isPlaying={isPlaying}
            isLoading={isLoading}
            onPerformerClick={person =>
              this._handleTrackSearch({
                text: person.publicName,
                flags: { people: true },
              })
            }
            onAuthorClick={person =>
              this._handleTrackSearch({
                text: person.publicName,
                flags: { people: true },
              })
            }
            onCheckboxClick={() => this._toggleWorklistItem(item.id)}
            onPlayClick={() => this._playTracks([item.track])}
            onMediaClick={this._handleMediaClick}
            actionButtons={this._renderActionButtons([item], worklist)}
          />
        );
      });
    }
  }

  render() {
    const {
      worklist: { fetching, error, data },
    } = this.props;
    return (
      <DataLoader
        loading={fetching}
        loadingIsBlocking={true}
        error={error}
        data={data}
        render={this._renderWorklist}
      />
    );
  }
}

export default withTranslation()(withRouter(connect(mapStateToProps, actions)(WorklistShow)));
